import { useCallback, useEffect, useMemo, useRef } from 'react'
import { ReactRenderer } from '@tiptap/react'
import type {
  SuggestionProps,
  SuggestionKeyDownProps,
} from '@tiptap/suggestion'
import {
  computePosition,
  autoUpdate,
  flip,
  shift,
  offset,
} from '@floating-ui/dom'
import type { MentionUser } from 'types/discussion'
import { MentionList } from './index'
import type { MentionListHandle } from './index'

const filterUsers = (users: MentionUser[], query: string): MentionUser[] => {
  const q = query.toLowerCase()
  return users
    .filter(user =>
      [user.label, user.display, user.description].some(field =>
        field?.toLowerCase().includes(q),
      ),
    )
    .slice(0, 8)
}

const buildMentionListProps = (
  props: SuggestionProps<MentionUser>,
  users: MentionUser[],
  loading: boolean,
  onInvite?: (query: string) => void,
) => ({
  ...props,
  items: filterUsers(users, props.query),
  loading,
  onInvite,
})

// Options added for mobile to handle mentions in its own native menu
export type MentionSuggestionOptions = {
  /** When true, skips rendering the DOM popup. The suggestion plugin still tracks the query. */
  suppressPopup?: boolean
  /** Fires with pre-filtered items on start/update, null on exit. */
  onFilteredItemsChange?: (items: MentionUser[] | null) => void
}

export function useMentionSuggestion(
  users: MentionUser[],
  usersLoading: boolean,
  onInvite?: (query: string) => void,
  options?: MentionSuggestionOptions,
) {
  const usersRef = useRef<MentionUser[]>(users)
  usersRef.current = users
  const usersLoadingRef = useRef(usersLoading)
  usersLoadingRef.current = usersLoading
  const onInviteRef = useRef(onInvite)
  onInviteRef.current = onInvite
  const onFilteredItemsChangeRef = useRef(options?.onFilteredItemsChange)
  onFilteredItemsChangeRef.current = options?.onFilteredItemsChange
  const suppressPopup = options?.suppressPopup ?? false

  // Stores the Suggestion plugin's command() so selectMention can call it in mobile
  const commandRef = useRef<
    ((attrs: { id: string | number; label: string }) => void) | null
  >(null)

  const activePopupRef = useRef<{
    element: HTMLElement | null
    cleanup: (() => void) | null
    component: ReactRenderer<MentionListHandle> | null
    props: SuggestionProps<MentionUser> | null
  }>({ element: null, cleanup: null, component: null, props: null })

  useEffect(() => {
    const { component, props } = activePopupRef.current
    if (!component || !props) return
    const updatedProps = buildMentionListProps(
      props,
      users,
      usersLoading,
      onInviteRef.current,
    )
    component.updateProps(updatedProps)
    activePopupRef.current.props = updatedProps
  }, [users, usersLoading])

  useEffect(() => {
    return () => {
      activePopupRef.current.cleanup?.()
      activePopupRef.current.element?.remove()
      activePopupRef.current.component?.destroy()
    }
  }, [])

  const selectMention = useCallback((id: number | string, label: string) => {
    commandRef.current?.({ id, label })
  }, [])

  const suggestion = useMemo(
    () => ({
      char: '@',
      items: ({ query }: { query: string }) =>
        filterUsers(usersRef.current, query),
      render: () => {
        let component: ReactRenderer<MentionListHandle> | null = null
        let element: HTMLElement | null = null
        let cleanup: (() => void) | null = null
        let getRect: (() => DOMRect) | null = null

        const positionEl = () => {
          if (!element || !getRect) return
          const virtualEl = { getBoundingClientRect: getRect }
          computePosition(virtualEl, element, {
            strategy: 'fixed',
            placement: 'bottom-start',
            middleware: [offset(4), flip(), shift({ padding: 8 })],
          }).then(({ x, y }) => {
            if (!element) return
            element.style.transform = `translate(${Math.round(
              x,
            )}px, ${Math.round(y)}px)`
          })
        }

        const teardown = () => {
          const componentToDestroy = component
          const menuWasOpened =
            componentToDestroy != null || commandRef.current != null

          if (menuWasOpened) {
            onFilteredItemsChangeRef.current?.(null)
          }

          commandRef.current = null
          cleanup?.()
          element?.remove()
          componentToDestroy?.destroy()
          if (activePopupRef.current.component === componentToDestroy) {
            activePopupRef.current = {
              element: null,
              cleanup: null,
              component: null,
              props: null,
            }
          }
          cleanup = null
          element = null
          getRect = null
          component = null
        }

        return {
          onStart: (props: SuggestionProps<MentionUser>) => {
            teardown()
            if (!props.clientRect) return

            commandRef.current = props.command

            if (suppressPopup) {
              onFilteredItemsChangeRef.current?.(props.items)
              return
            }

            const updatedProps = buildMentionListProps(
              props,
              usersRef.current,
              usersLoadingRef.current,
              onInviteRef.current,
            )
            component = new ReactRenderer(MentionList, {
              props: updatedProps,
              editor: props.editor,
            })
            element = document.createElement('div')
            element.style.position = 'fixed'
            element.style.top = '0'
            element.style.left = '0'
            element.style.zIndex = '9999'
            element.appendChild(component.element)
            // Keep Radix components inside the editor's Theme subtree so they
            // inherit its tokens. Fixed positioning keeps Floating UI's
            // viewport-relative coordinates correct even when this container
            // is nested inside a positioned element.
            const themeContainer =
              props.editor.view.dom.closest('.radix-themes')
            const popupContainer = themeContainer ?? document.body
            popupContainer.appendChild(element)

            getRect = props.clientRect as () => DOMRect
            const virtualEl = { getBoundingClientRect: getRect }

            function handleClickOutside(event: MouseEvent) {
              if (element && !element.contains(event.target as Node)) {
                teardown()
              }
            }
            document.addEventListener('mousedown', handleClickOutside)

            // autoUpdate returns a cleanup function
            const stopAutoUpdate = autoUpdate(virtualEl, element, positionEl)
            cleanup = () => {
              document.removeEventListener('mousedown', handleClickOutside)
              stopAutoUpdate()
            }

            activePopupRef.current = {
              element,
              cleanup,
              component,
              props: updatedProps,
            }
          },

          onUpdate: (props: SuggestionProps<MentionUser>) => {
            commandRef.current = props.command

            if (suppressPopup) {
              onFilteredItemsChangeRef.current?.(props.items)
              return
            }

            const updatedProps = buildMentionListProps(
              props,
              usersRef.current,
              usersLoadingRef.current,
              onInviteRef.current,
            )
            component?.updateProps(updatedProps)
            activePopupRef.current.props = updatedProps
            if (props.clientRect) {
              getRect = props.clientRect as () => DOMRect
              positionEl()
            }
          },

          onKeyDown: (props: SuggestionKeyDownProps) => {
            if (suppressPopup) return false

            if (props.event.key === 'Escape') {
              // Dismiss only the dropdown. Without this the event bubbles to
              // react-modal's close-on-escape and closes the whole modal,
              // losing the draft.
              props.event.stopPropagation()
              teardown()
              return true
            }
            if (props.event.key === 'Tab') {
              // When the empty state offers Invite, move focus onto it (keeping
              // the popup open) so keyboard users can reach it; there are no
              // items to tab through, so tabbing out otherwise closes the popup.
              const inviteButton = element?.querySelector<HTMLButtonElement>(
                '[data-mention-invite]',
              )
              if (inviteButton) {
                props.event.preventDefault()
                inviteButton.focus()
                return true
              }
              teardown()
              return false
            }
            return component?.ref?.onKeyDown(props) ?? false
          },

          onExit: () => {
            teardown()
          },
        }
      },
    }),
    [suppressPopup],
  )

  return { suggestion, selectMention }
}
